import * as React from 'react'
import { Mail, MapPin, Phone } from 'lucide-react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Badge } from '@/components/ui/badge'
import { CopyButton } from '@/components/ui/copy-button'
import type { ClientPhone, PatientRecord } from '@/features/patients/types'
import {
  clientAddress,
  clientPhones,
  isPhoneInactive,
} from '@/features/patients/utils/clientDisplay'
import { phoneKey } from '@/features/patients/utils/clientActivity'
import { formatPhoneForDisplay } from '@/utils/phone'

function ContactRow({
  icon,
  value,
  copyLabel,
  tags,
}: {
  icon: React.ReactNode
  value: string
  copyLabel: string
  tags?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2.5 border-t py-2 text-sm first:border-t-0 first:pt-0.5">
      <span className="text-neutral-solid shrink-0 [&>svg]:size-4">{icon}</span>
      <span className="min-w-0 flex-1 break-words">{value}</span>
      {tags}
      <CopyButton
        content={value}
        label={copyLabel}
        className="text-neutral-hover-border hover:text-primary-solid shrink-0"
      />
    </div>
  )
}

function PhoneTags({
  phone,
  isPrimary,
  unanswered,
}: {
  phone: ClientPhone
  isPrimary: boolean
  /** Latest calls in a row to this number that nobody answered. */
  unanswered: number
}) {
  return (
    <span className="flex shrink-0 flex-wrap gap-1">
      {phone.label ? <Badge variant="secondary">{phone.label}</Badge> : null}
      {isPhoneInactive(phone) ? (
        <Badge variant="warning">{phone.status}</Badge>
      ) : null}
      {unanswered > 0 ? (
        <Badge variant="warning">no answer ×{unanswered}</Badge>
      ) : null}
      {isPrimary ? <Badge>primary</Badge> : null}
    </span>
  )
}

/**
 * Phone, email and address — the fields the sheet exists to surface, so they
 * lead the Overview rather than sitting fourth in a demographics list.
 *
 * Every number on the record is listed, not just the first: `Phone` is a JSONB
 * array and roughly one client in fourteen has a second or third line. The
 * `primary` badge marks the number relay flattens into the table column, so a
 * caller can tell which one the rest of the app means.
 */
export function ClientContactCard({
  patient,
  unansweredByPhone,
}: {
  patient: PatientRecord
  /** Unanswered-call streaks keyed by `phoneKey`; empty when calls are hidden. */
  unansweredByPhone: ReadonlyMap<string, number>
}) {
  const phones = clientPhones(patient)
  const address = clientAddress(patient)
  const hasAny = phones.length > 0 || !!patient.email || !!address

  return (
    <SheetSectionCard
      title="Contact"
      body={
        hasAny ? (
          <div className="flex flex-col">
            {phones.map((phone, index) => (
              <ContactRow
                key={phone.phone_number}
                icon={<Phone />}
                value={formatPhoneForDisplay(phone.phone_number)}
                copyLabel="Copy phone number"
                tags={
                  <PhoneTags
                    phone={phone}
                    isPrimary={index === 0 && phones.length > 1}
                    unanswered={
                      unansweredByPhone.get(phoneKey(phone.phone_number)) ?? 0
                    }
                  />
                }
              />
            ))}
            {patient.email ? (
              <ContactRow
                icon={<Mail />}
                value={patient.email}
                copyLabel="Copy email address"
              />
            ) : null}
            {address ? (
              <ContactRow
                icon={<MapPin />}
                value={address}
                copyLabel="Copy address"
              />
            ) : null}
          </div>
        ) : (
          <p className="text-muted-foreground py-2 text-center text-sm">
            No contact details on file
          </p>
        )
      }
    />
  )
}
